import { pageSectionFields, pageSectionTypes } from "./storefront-sections.js";
/** The online store in the Admin (docs/specs/storefront.md, Phase 4). */
export const storefront = {
    title: "Online store",
    description: "Your own shop on the web, selling from the same stock as your branches and POS.",
    status: {
        live: "Live",
        off: "Off",
        notInPlan: "Not in your plan",
    },
    shopAddress: "Shop address",
    openShop: "Open shop",
    copyAddress: "Copy address",
    copied: "Address copied.",
    notInPlan: "Your plan does not include the online store. Upgrade the plan to turn it on.",
    enable: "Turn the online store on",
    enableHint: "Shoppers can visit the shop and place orders while it is on.",
    stockTitle: "Stock for online orders",
    stockHint: "Online orders take stock from these branches. None chosen: your default online-order location.",
    noLocations: "No active locations yet.",
    defaultLocation: "Default online-order location",
    showStockCount: "Show how many are left",
    showStockCountHint: "Off: shoppers only see in stock, few left or out of stock.",
    showSoldCount: "Show how many sold",
    showSoldCountHint: "Shows “10+ sold in the last 30 days” (or 50+, 100+) on products, counted from delivered orders only. Nothing shows below 10.",
    hidePoweredBy: "Hide “Powered by Sobthik”",
    hidePoweredByHint: "Your shop shows only your own name in the footer.",
    paymentTitle: "Payment",
    codEnabled: "Cash on delivery",
    codEnabledHint: "Shoppers can pay the delivery person.",
    checkoutOtp: "Ask for an SMS code before ordering",
    checkoutOtpHint: "Shoppers confirm their phone with a code sent by SMS. Off: they order with the phone number alone; cash-on-delivery orders then arrive as Received and hold no stock until you call and confirm them.",
    contactTitle: "Contact shown in the shop",
    contactPhone: "Phone",
    contactPhoneHint: "Empty: the business phone from Business settings.",
    contactEmail: "E-mail",
    saved: "Online store settings saved.",
    conflict: "Someone else changed these settings. They were reloaded.",
    domains: {
        title: "Your own domain",
        description: "Use an address you own, like shop.yourname.com. You need access to its DNS settings.",
        notInPlan: "Your plan does not include an own domain. Upgrade the plan to connect one.",
        add: "Connect domain",
        hostname: "Domain",
        hostnamePlaceholder: "shop.example.com",
        apexHint: "Use a name like www.example.com or shop.example.com; a bare example.com works only if your DNS provider supports CNAME flattening.",
        empty: "No own domain yet. Shoppers use the shop address above.",
        platformAddress: "Platform address: {{host}}",
        status: {
            pending_dns: "Waiting for DNS",
            verifying: "Checking",
            active: "Live",
            failed: "Not working",
            blocked: "Blocked",
        },
        primary: "Main address",
        makePrimary: "Make main address",
        clearPrimary: "Use the platform address as main",
        check: "Check again",
        remove: "Remove",
        removeTitle: "Remove {{host}}?",
        removeBody: "The shop stops opening on this domain. You can connect it again later.",
        recordsTitle: "Add these records at your DNS provider",
        recordType: "Type",
        recordName: "Name",
        recordValue: "Value",
        recordsHint: "DNS changes can take from a few minutes to a few hours. We check automatically for 72 hours.",
        lastChecked: "Last checked {{when}}",
        added: "Domain added. Now add the DNS records below.",
        checking: "Checking the domain…",
        primarySet: "{{host}} is now the main address.",
        primaryCleared: "The platform address is the main address again.",
        removed: "Domain removed.",
    },
    product: {
        title: "Online store",
        description: "Show this product in your online shop.",
        published: "Show in the online store",
        publishedHint: "Shoppers can find and buy it online.",
        archived: "Archived products are not shown online.",
        slug: "Web address",
        slugHint: "Letters, numbers and dashes. Changing it keeps the old address working.",
        seoTitle: "Search title",
        seoTitleHint: "Up to 70 characters. Empty: the product name.",
        seoDescription: "Search description",
        seoDescriptionHint: "Up to 170 characters. Empty: the start of the description.",
        count: "{{count}}/{{max}}",
        saved: "Online store settings of the product saved.",
    },
    extras: {
        title: "Product page details",
        description: "Specifications, a size chart and products to show with this one in your online shop.",
        specs: "Specifications",
        specsHint: "Groups of facts shoppers compare: material, battery, ingredients, dimensions…",
        group: "Group title",
        label: "Name",
        value: "Value",
        addRow: "Add a line",
        removeRow: "Remove this line",
        addGroup: "Add a group",
        removeGroup: "Remove this group",
        sizeGuide: "Size chart",
        hasSizeGuide: "Show a size chart on the product page",
        unit: "Unit",
        units: { inch: "Inches", cm: "Centimetres", none: "No unit" },
        column: "Column {{n}}",
        cell: "Row {{row}}, {{column}}",
        addSizeRow: "Add a size",
        addColumn: "Add a column",
        removeLastRow: "Remove last size",
        removeLastColumn: "Remove last column",
        howToMeasure: "How to measure",
        picks: "Pairs well with",
        picksHint: "Up to 8 products shown with this one, in this order (\u201ccomplete the look\u201d, bundles).",
        findProduct: "Find a product to add",
        removePick: "Remove {{name}}",
        saved: "Product page details saved.",
    },
    bulk: {
        selected: "{{count}} selected",
        publish: "Show online",
        unpublish: "Hide online",
        done: "{{count}} products changed.",
        skipped: "{{count}} archived products were skipped.",
    },
    gateways: {
        title: "Online payments",
        description: "Let shoppers pay with your own bKash, Nagad or SSLCommerz merchant account. Money goes straight to you.",
        names: { bkash: "bKash", nagad: "Nagad", sslcommerz: "SSLCommerz (cards & wallets)" },
        enabled: "Take payments with {{name}}",
        sandbox: "Test mode (the gateway's sandbox)",
        account: "Record the money in",
        chooseAccount: "Choose a payment account",
        configured: "Credentials saved",
        notConfigured: "No credentials yet",
        replace: "Enter new credentials",
        fields: {
            storeId: "Store ID",
            storePassword: "Store password",
            appKey: "App key",
            appSecret: "App secret",
            username: "Username",
            password: "Password",
            merchantId: "Merchant ID",
            merchantPrivateKey: "Merchant private key",
            nagadPublicKey: "Nagad public key",
        },
        secretHint: "Stored encrypted. It is never shown again.",
        saved: "{{name}} settings saved.",
    },
    coupons: {
        title: "Coupons",
        description: "Discount codes and automatic offers for your online store.",
        new: "New coupon",
        edit: "Edit {{code}}",
        empty: "No coupons yet.",
        code: "Code",
        kind: "Type",
        kinds: { percent: "Percent off", amount: "Amount off", free_delivery: "Free delivery" },
        percent: "Percent",
        amount: "Amount (৳)",
        maxDiscount: "At most (৳, optional)",
        minOrder: "Minimum order (৳)",
        startsAt: "Starts (optional)",
        endsAt: "Ends (optional)",
        limitTotal: "Uses in all (optional)",
        limitPerPhone: "Uses per phone (optional)",
        autoApply: "Apply automatically when the order qualifies",
        paused: "Paused",
        active: "Active",
        used: "Used",
        value: "Value",
        saved: "Coupon {{code}} saved.",
        unlimited: "No limit",
    },
    reviews: {
        title: "Reviews",
        description: "What shoppers say. New 4–5★ reviews go live at once unless you change it below.",
        autoPublish: "Publish new reviews",
        modes: {
            none: "Only after I check them",
            good: "4 and 5 stars at once, others after I check",
            all: "All at once",
        },
        empty: "No reviews to show.",
        pending: "Waiting",
        published: "Live",
        hidden: "Hidden",
        publish: "Publish",
        hide: "Hide",
        reply: "Reply (optional)",
        saved: "Review updated.",
        filters: { all: "All", pending: "Waiting", published: "Live", hidden: "Hidden" },
    },
    appearance: {
        title: "Appearance",
        description: "Colours, menus and texts of your online shop. Changes stay in a draft until you publish.",
        open: "Change the look",
        colors: "Colours",
        primary: "Main colour (buttons, links)",
        accent: "Accent colour",
        shape: "Corners",
        radii: { none: "Square", small: "Slightly round", medium: "Round", large: "Very round" },
        headingFont: "Heading font",
        fonts: { inter: "Clean (Inter)", serif: "Classic (serif)", rounded: "Friendly (rounded)" },
        announcement: "Announcement bar",
        announcementOn: "Show the announcement bar",
        announcementText: "Text",
        link: "Link (optional)",
        headerMenu: "Header menu",
        footerMenu: "Footer menu",
        menuHint: "Empty header menu: your categories are shown.",
        addLink: "Add link",
        label: "Label",
        href: "Link (/products or https://…)",
        remove: "Remove",
        social: "Social links",
        policies: "Policy pages",
        policyHint: "Plain text; leave a blank line between paragraphs. Empty pages are not shown.",
        saveDraft: "Save draft",
        preview: "Preview",
        publish: "Publish",
        draftSaved: "Draft saved.",
        published: "Your shop now shows the new look.",
        lastPublished: "Live since {{when}}",
        neverPublished: "Not published yet — the shop uses the default look.",
        // ST-12: customise panel and live preview
        style: "Theme style",
        styleHint: "Only what your theme offers. Every choice keeps text readable and pages fast.",
        baseTheme: "Theme: {{name}}",
        changeTheme: "Change theme",
        palette: "Colour set",
        customPalette: "Your own colours",
        contrast: "Contrast {{ratio}}:1",
        contrastGood: "Easy to read",
        contrastSample: "Aa",
        contrastLow: "Too light for white text — choose a darker main colour",
        fontPairing: "Fonts",
        fontPairings: { signature: "Theme fonts", classic: "Classic (fastest)" },
        fontSample: "Aa অআ ক্ষ",
        layout: "Layout and behaviour",
        optionNames: {
            headerStyle: "Header",
            cardStyle: "Product cards",
            buttonStyle: "Buttons",
            colorScheme: "Light or dark",
            density: "Spacing",
            motion: "Animation",
            stickyBuyBar: "Buy bar on product pages (phones)",
            quickAdd: "Quick add on product cards",
            showSecondImage: "Second picture on hover",
            bottomNav: "Bottom tab bar (phones)",
            cartOpens: "After adding to cart",
            announcementStyle: "Announcement messages",
            headerScroll: "Header while scrolling",
        },
        optionValues: {
            classic: "Classic",
            centered: "Centred",
            split: "Split",
            minimal: "Minimal",
            search_first: "Search first",
            standard: "Standard",
            overlay: "Text on picture",
            bordered: "Bordered",
            list: "List",
            solid: "Solid",
            outline: "Outline",
            pill: "Round",
            sharp: "Square",
            light: "Light",
            dark: "Dark",
            compact: "Compact",
            comfortable: "Comfortable",
            airy: "Airy",
            full: "Full",
            subtle: "Subtle",
            off: "Off",
            on: "On",
            drawer: "Open the cart",
            toast: "Show a short message",
            static: "One at a time",
            rotating: "Take turns",
            marquee: "Scrolling line",
            sticky: "Always visible",
            hide_on_scroll: "Hide when scrolling down",
        },
        moreMessages: "More messages (shown in turn)",
        addMessage: "Add message",
        trending: "Popular searches",
        trendingHint: "Shown in the search box before shoppers type, up to 8.",
        addSearch: "Add search",
        chat: "Chat button",
        chatHint: "A button in the corner of every page. Add the number or page above first.",
        chatOptions: { off: "Off", whatsapp: "WhatsApp", messenger: "Messenger" },
        trust: "Trust badges",
        trustHint: "Short promises you keep, shown on product pages (up to 4), e.g. “Original products”.",
        addBadge: "Add badge",
        previewTitle: "Live preview",
        previewHint: "Shows your saved draft. Shoppers still see the published look.",
        updatePreview: "Save and update preview",
        unsaved: "You have changes the preview does not show yet.",
        openPreview: "Open in a new tab",
        devices: { phone: "Phone", tablet: "Tablet", desktop: "Desktop" },
        previewFrame: "Preview of your shop on a {{device}}",
    },
    takenDown: "The platform closed your online store. Contact support to reopen it.",
    pages: {
        title: "Pages",
        description: "Your home page, extra pages and single-product landing pages for ads.",
        new: "New page",
        empty: "No pages yet. Start with your home page.",
        fromHome: "Start from the home page's layout",
        fromHomeHint: "Copies its sections. The order form sells the product you choose here, and its buttons lead to this page's form.",
        kind: "Type",
        kinds: { home: "Home page", custom: "Page", landing: "Landing page" },
        kindHints: {
            home: "The first page shoppers see. One per shop. Selling one product? Add an order form here.",
            custom: "About us, size guide, contact — any page with your own sections.",
            landing: "One product with an order form — for Facebook ads. Cash on delivery, no code by SMS.",
        },
        pageTitle: "Title",
        slug: "Address",
        slugHint: "Small letters, digits and dashes, e.g. eid-offer",
        create: "Create page",
        created: "Page created.",
        live: "Live",
        draftOnly: "Draft",
        updated: "Changed {{when}}",
        open: "Edit",
        back: "All pages",
        seo: "Search engines",
        seoTitle: "Title in search results",
        seoDescription: "Description in search results",
        sections: "Sections",
        sectionsHint: "Shoppers see the sections from top to bottom.",
        noSections: "No sections yet. Add one below.",
        add: "Add section",
        up: "Move up",
        down: "Move down",
        remove: "Remove",
        types: pageSectionTypes,
        v2: pageSectionFields,
        fields: {
            title: "Heading",
            subtitle: "Subheading",
            buttonLabel: "Button text",
            buttonHref: "Button link (/products or https://…)",
            image: "Image",
            upload: "Upload image",
            uploading: "Uploading…",
            uploadFailed: "The image could not be uploaded.",
            removeImage: "Remove image",
            uploadVideo: "Upload video",
            videoAdded: "Video added",
            videoFailed: "The video could not be uploaded (MP4, up to 10 MB).",
            removeVideo: "Remove video",
            imageSide: "Image side",
            sides: { left: "Left", right: "Right" },
            body: "Text",
            category: "Category",
            allCategories: "All categories",
            sort: "Order",
            sorts: {
                newest: "Newest first",
                price_asc: "Price: low to high",
                price_desc: "Price: high to low",
                name: "Name",
            },
            limit: "How many",
            question: "Question",
            answer: "Answer",
            name: "Name",
            quote: "What they said",
            addItem: "Add",
            endsAt: "Ends at",
            product: "Product sold on this page",
            noProduct: "Choose a product.",
            allVariants: "Shoppers choose on the form from all its variants: {{values}}.",
            change: "Change",
        },
        saveDraft: "Save draft",
        preview: "Preview",
        publish: "Publish",
        unpublish: "Take offline",
        viewLive: "Open page",
        draftSaved: "Draft saved.",
        published: "The page is live.",
        unpublished: "The page is offline.",
    },
    themes: {
        title: "Shop themes",
        description: "Ready-made designs for your kind of business. One click sets up pages, categories, menus and policies.",
        all: "All",
        types: {
            fashion: "Fashion",
            beauty: "Beauty",
            electronics: "Gadgets",
            food: "Food",
            home: "Home",
            kids: "Kids",
            accessories: "Accessories",
            books: "Books",
            any: "Any business",
        },
        premium: "Premium",
        current: "In use",
        use: "Use this theme",
        demo: "Live demo",
        customise: "Customise",
        shotDesktop: "{{name}} on a computer",
        shotPhone: "{{name}} on a phone",
        youGet: "You get",
        pagesLine: "Pages: {{pages}}",
        categoriesLine: "Categories: {{categories}}",
        confirmTitle: "Use the {{name}} theme?",
        confirmBody: "Your products, settings and own texts stay. Missing pages and categories are added, and the shop is turned on.",
        replacePages: "Also replace my existing home, about and contact pages with this theme's layout",
        sampleProducts: "Add sample products so the shop does not look empty (you can archive them later)",
        applying: "Setting up your shop…",
        applied: "Done! Your shop uses the new theme.",
        openShop: "Open my shop",
        rollback: "Restore the previous look",
        rolledBack: "The previous look is back.",
        preview: "Sample",
        names: {
            fashion_women: "Fashion — Women",
            fashion_men: "Fashion — Men",
            cosmetics: "Cosmetics & skincare",
            gadgets: "Gadgets & electronics",
            grocery: "Grocery & organic food",
            home_kitchen: "Home & kitchen",
            kids_baby: "Kids & baby",
            jewellery: "Jewellery & accessories",
            home_food: "Food & home kitchen",
            books: "Bookshop",
            single_product: "Single product (FB ads)",
            minimal: "Minimal",
        },
        for: {
            fashion_women: "Saree, three-piece, kurti, abaya, boutique",
            fashion_men: "Panjabi, shirt, T-shirt, polo, jeans",
            cosmetics: "Korean/Japanese skincare, makeup, perfume",
            gadgets: "Earbuds, smart watches, chargers, power banks",
            grocery: "Honey, ghee, mustard oil, dates, rice",
            home_kitchen: "Cookware, storage, bed sheets, décor",
            kids_baby: "Baby clothes, toys, baby care",
            jewellery: "Imitation jewellery, bags, watches",
            home_food: "Cakes, pitha, frozen and home-made food",
            books: "Books, hard copy and eBooks, authors, publishers",
            single_product: "One product sold through Facebook or TikTok ads",
            minimal: "Any shop — clean and fast",
        },
    },
    tracking: {
        title: "Ads and analytics",
        description: "Facebook Pixel with the Conversions API, and Google Analytics 4. Purchases are counted once even when both the browser and the server report them.",
        pixelId: "Facebook Pixel ID",
        pixelHint: "Digits only, from Meta Events Manager.",
        capiToken: "Conversions API access token",
        capiHint: "Saved encrypted and never shown again. Leave empty to keep the saved one.",
        capiSaved: "A token is saved.",
        removeToken: "Remove the token",
        testCode: "Test event code (optional)",
        testHint: "Only while checking the setup in Meta's Test events; remove it afterwards.",
        ga4Id: "Google Analytics 4 measurement ID",
        ga4Hint: "Looks like G-XXXXXXX.",
        save: "Save",
        saved: "Ads and analytics settings saved.",
        invalidPixel: "A Pixel ID is 10 to 20 digits.",
        invalidGa4: "A measurement ID looks like G-XXXXXXX.",
    },
    accounts: {
        title: "Shopper accounts",
        description: "How shoppers sign up and sign in to your online store.",
        signUpEmail: "Sign up with an email",
        signUpEmailHint: "Shoppers can make an account with their email address and a password.",
        signUpPhone: "Sign up with a mobile number",
        signUpPhoneHint: "Shoppers can make an account with their mobile number and a password.",
        signUpEmailCode: "Ask for an email code at sign-up",
        signUpEmailCodeHint: "Off: the account is made at once and the email can be confirmed later.",
        signUpPhoneCode: "Ask for an SMS code at sign-up",
        signUpPhoneCodeHint: "Each code is an SMS from your credits. Off: the number can be confirmed later.",
        phoneCodeSignIn: "Sign in with an SMS code",
        phoneCodeSignInHint: "Mobile accounts may sign in with a code instead of the password (uses SMS credits).",
        google: "Google sign-in",
        googleHint: "Show “Continue with Google”. Uses your own Google client, made in your Google account.",
        clientId: "Google Client ID",
        clientIdPlaceholder: "123456789012-abc.apps.googleusercontent.com",
        guideTitle: "How to get a Client ID",
        guide1: "Open Google Cloud Console (console.cloud.google.com) and create a project for your shop.",
        guide2: "Go to “Google Auth Platform” → Branding: your shop's name, support email and logo. Audience: External, then publish.",
        guide3: "Go to Clients → Create client → Application type “Web application”.",
        guide4: "Under “Authorized JavaScript origins” add each address below.",
        guide5: "Create, copy the Client ID and paste it here, then save.",
        origins: "Your shop's addresses",
        copy: "Copy",
        copied: "Copied",
        save: "Save sign-in settings",
        saved: "Sign-in settings saved.",
        viewAccounts: "View shopper accounts",
        listTitle: "Online accounts",
        listDescription: "Shoppers who made an account in your online store.",
        search: "Name, email or mobile",
        empty: "No shopper accounts yet.",
        columns: {
            name: "Name",
            email: "Email",
            phone: "Mobile",
            methods: "Sign-in",
            status: "Status",
            lastSignIn: "Last sign-in",
            created: "Joined",
        },
        password: "Password",
        googleMethod: "Google",
        verified: "verified",
        active: "Active",
        disabled: "Disabled",
        never: "Never",
        disable: "Disable",
        enable: "Enable",
        signOut: "Sign out everywhere",
        disabledToast: "The account is disabled and signed out.",
        enabledToast: "The account can sign in again.",
        signedOutToast: "The account is signed out on every device.",
        confirmDisable: "Disable {{name}}? They are signed out and cannot sign in until you enable the account.",
    },
};
//# sourceMappingURL=storefront.js.map